import { useMemo } from 'react';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { useAppSelector } from '@/redux/hooks';
import { VersionGroupedByParent } from '@/redux/slices/slice_types';
import { selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { TitleVersionChip } from '@/exports/columnCommonComponents';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { getAllVersions, getDepthToVersion, getVersionFromGroup } from '@/_utils/bulkSearchUtils';

interface OPVersionSelectionsListProps {
  selectedVersions: VersionGroupedByParent;
}

const OPVersionSelectionsListFromLineage = ({ selectedVersions }: OPVersionSelectionsListProps) => {
  const versionsByKey = useAppSelector(selectVersionKeys);

  const versionByKeyFromSelections = useMemo(() => {
    return getAllVersions(selectedVersions);
  }, [selectedVersions]);

  const selectTitleById = (id: string) => {
    const titleKey = String(id).substring(1);
    return versionsByKey[+titleKey] || versionByKeyFromSelections.get(+titleKey) || {};
  };

  const ChildEntity = ({ parent }: { parent: VersionGroupedByParent }) => {
    const depth = getDepthToVersion(parent, Object.keys(parent)[0]); // 2 season, 1 episode
    const parentKeys = Object.keys(parent).sort((a, b) => {
      const version1 = getVersionFromGroup(parent, a);
      const version2 = getVersionFromGroup(parent, b);
      if (depth === 2) {
        return Number(version1?.lineage_meta?.season_sequence_order) - Number(version2?.lineage_meta?.season_sequence_order);
      }
      return version1?.lineage_meta?.episode_sequence_order - version2?.lineage_meta?.episode_sequence_order;
    });


    return (
      <Box>
        {parentKeys.map((parentKey) => {
          const content = parent[parentKey];

          if (typeof content.name === "string" && content?.entity_type === 'version') {
            return (
              <Box key={parentKey}>
                <Grid container alignItems="center" sx={{ py: 0.5 }}>
                  <Grid item maxWidth="50%">
                    <Typography sx={{ wordBreak: 'break-all' }}>
                      <span style={{ opacity: 0.6, paddingRight: 5 }}>E{!isNaN(content.sequence_order) ? Number(content.sequence_order) : '?'}:</span>
                      {content.name}
                    </Typography>
                  </Grid>
                  <Grid item flex="auto">
                    <Divider sx={{ borderColor: 'action.selected', borderStyle: 'dashed', mx: 2 }} />
                  </Grid>
                  <Grid item>
                    <TitleVersionChip version={content as TitleEntityV2} size="small" withTooltip />
                  </Grid>
                </Grid>
              </Box>
            );
          } else if (depth === 2) {
            const versionEntity = getVersionFromGroup(parent, parentKey);
            return (
              <Box key={parentKey}>
                <Typography fontWeight="bold">{versionEntity?.lineage_meta?.season_name}</Typography>
                {parent[parentKey] && <ChildEntity key={parentKey} parent={parent[parentKey]} />}
              </Box>
            );
          } else {
            return (
              <Box key={parentKey}>
                {(() => {
                  const parentContent = parent[parentKey];
                  const filteredKeys = Object.keys(parentContent || {});

                  return filteredKeys
                    .map((versionKey) => {
                      const version = selectTitleById(versionKey);
                      return (
                        <Box key={versionKey}>
                          <Grid container key={versionKey} alignItems="center" sx={{ py: 0.5 }}>
                            <Grid item maxWidth="50%">
                              <Typography sx={{ wordBreak: 'break-all' }}>
                                <span style={{ opacity: 0.6, paddingRight: 5 }}>
                                  E{version?.lineage_meta?.episode_sequence_order}:
                                </span>
                                {version?.lineage_meta?.episode_name}
                              </Typography>
                            </Grid>
                            <Grid item flex="auto">
                              <Divider sx={{ borderColor: 'action.selected', borderStyle: 'dashed', mx: 2 }} />
                            </Grid>
                            <Grid item>
                              <TitleVersionChip key={versionKey} version={version} size="small" withTooltip />
                            </Grid>
                          </Grid>
                        </Box>
                      );
                    });
                })()}
              </Box>
            );
          }
        })}
      </Box>
    );
  };

  return <ChildEntity parent={selectedVersions} />;
};

export default OPVersionSelectionsListFromLineage;
